// Plans & Prep API — wraps curriculum endpoints with a simpler upload + generate flow.

function getApiBaseUrl() {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  }
  const host = window.location.hostname;
  if (host.includes('fastgrader.org')) return 'https://backend.fastgrader.org/api';
  return `http://${host}:5000/api`;
}

const BASE = getApiBaseUrl();
const token = () => localStorage.getItem('authToken');

function jsonHeaders() {
  return {
    'Content-Type': 'application/json',
    ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
  };
}

async function req(method, path, body) {
  const res = await fetch(`${BASE}/curriculum${path}`, {
    method,
    headers: jsonHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || `Request failed (${res.status})`);
  return data;
}

export const plansPrepApi = {
  browseClasses: (grade) => req('GET', `/browse/classes?grade=${encodeURIComponent(grade)}`),
  browseSubjects: (className) => req('GET', `/browse/subjects?class=${encodeURIComponent(className)}`),

  addCourse: (className, subject) => req('POST', '/courses', { class: className, subject }),

  listChapters: (courseId) => req('GET', `/courses/${courseId}/chapters`),

  getChapter: (id) => req('GET', `/chapters/${id}`),

  getDays: (id) => req('GET', `/chapters/${id}/days`),

  listNoteVersions: (id) => req('GET', `/chapters/${id}/note-versions`),

  activateNoteVersion: (id, versionId) => req('POST', `/chapters/${id}/note-versions/${versionId}/activate`),

  renameNoteVersion: (id, versionId, label) => req('PATCH', `/chapters/${id}/note-versions/${versionId}`, { label }),

  deleteNoteVersion: (id, versionId) => req('DELETE', `/chapters/${id}/note-versions/${versionId}`),

  getGeneration: (id) => req('GET', `/chapters/${id}/generation`),

  // --- QA ledger -----------------------------------------------------------
  /** Issues for a chapter, optionally narrowed to one day / kind / status. */
  getQaIssues: (id, { day, kind, status, includeResolved } = {}) => {
    const q = new URLSearchParams();
    if (day != null) q.set('day', String(day));
    if (kind) q.set('kind', kind);
    if (status) q.set('status', status);
    if (includeResolved) q.set('include_resolved', '1');
    const qs = q.toString();
    return req('GET', `/chapters/${id}/qa/issues${qs ? `?${qs}` : ''}`);
  },

  getQaSummary: (id) => req('GET', `/chapters/${id}/qa/summary`),

  /** status: 'accepted' | 'wontfix' | 'open' | 'needs_human' */
  setQaIssueStatus: (id, issueId, status, resolution) => req(
    'PATCH', `/chapters/${id}/qa/issues/${issueId}`, { status, resolution },
  ),

  /** Every question in a day with its issues and whether it is frozen. */
  getQaBlocks: (id, { day, kind } = {}) => {
    const q = new URLSearchParams();
    if (day != null) q.set('day', String(day));
    if (kind) q.set('kind', kind);
    const qs = q.toString();
    return req('GET', `/chapters/${id}/qa/blocks${qs ? `?${qs}` : ''}`);
  },

  /** Freeze one verified answer so later generations restore it verbatim. */
  approveQaBlock: (id, { day, kind, blockId, note }) => req(
    'POST', `/chapters/${id}/qa/approvals`, { day, kind, blockId, note },
  ),

  revokeQaApproval: (id, fingerprint, { day, kind } = {}) => {
    const q = new URLSearchParams();
    if (day != null) q.set('day', String(day));
    if (kind) q.set('kind', kind);
    const qs = q.toString();
    return req('DELETE', `/chapters/${id}/qa/approvals/${fingerprint}${qs ? `?${qs}` : ''}`);
  },

  /** Deterministic re-check of saved text — no model calls, instant and free. */
  recheckQa: (id, { day, kind, apply } = {}) => req(
    'POST', `/chapters/${id}/qa/recheck`, { day, kind, apply: !!apply },
  ),

  getQueue: () => req('GET', '/queue'),

  getCapabilities: () => req('GET', '/capabilities'),

  extractText: (id, opts = {}) => {
    const body = {};
    if (opts.pages != null && opts.pages !== '') body.pages = opts.pages;
    else if (opts.page != null && opts.page !== '') body.pages = opts.page;
    if (opts.provider) body.provider = opts.provider;
    if (opts.local_model) body.local_model = opts.local_model;
    if (opts.local_host) body.local_host = opts.local_host;
    return req('POST', `/chapters/${id}/extract-text`, Object.keys(body).length ? body : undefined);
  },

  /** Split Chapter OCR into per-topic text only (no notes generation). */
  divideTopics: (id, opts = {}) => {
    const body = { provider: opts.provider || 'local' };
    if (opts.provider === 'local' && opts.local_model) body.local_model = opts.local_model;
    if (opts.provider === 'local' && opts.local_host) body.local_host = opts.local_host;
    return req('POST', `/chapters/${id}/divide-topics`, body);
  },

  generate: (id, opts = {}) => {
    const body = { provider: opts.provider || 'cursor' };
    if (opts.step) body.step = opts.step;
    if (opts.day != null) body.day = opts.day;
    if (opts.provider === 'local' && opts.local_model) body.local_model = opts.local_model;
    if (opts.provider === 'local' && opts.local_host) body.local_host = opts.local_host;
    return req('POST', `/chapters/${id}/generate`, body);
  },

  stopGeneration: (id) => req('POST', `/chapters/${id}/generate/stop`),

  getOutput: (id, step, day) => req('GET', `/chapters/${id}/output/${step}${day != null ? `?day=${day}` : ''}`),

  updateChapter: (id, { title, target_days, chapter_no }) => {
    const body = { title, target_days };
    if (chapter_no != null && chapter_no !== '') body.chapter_no = chapter_no;
    return req('PUT', `/chapters/${id}`, body);
  },

  updateChapterText: (id, text) => req('PUT', `/chapters/${id}/text`, { text }),

  deleteChapter: (id) => req('DELETE', `/chapters/${id}`),

  listChapterPages: (id) => req('GET', `/chapters/${id}/pages`),

  fetchChapterPageBlob: async (id, page) => {
    const res = await fetch(`${BASE}/curriculum/chapters/${id}/pages/${page}`, {
      headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.message || `Page load failed (${res.status})`);
    }
    return res.blob();
  },

  deleteChapterPage: (id, page) => req('DELETE', `/chapters/${id}/pages/${page}`),

  appendChapterPages: async (id, files) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    const res = await fetch(`${BASE}/curriculum/chapters/${id}/pages`, {
      method: 'POST',
      headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `Upload failed (${res.status})`);
    return data;
  },

  uploadPack: async (courseId, { files, title, targetDays, chapterNo }) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append('files', f));
    if (title) fd.append('title', title);
    if (targetDays != null && targetDays !== '') fd.append('target_days', String(targetDays));
    if (chapterNo != null && chapterNo !== '') fd.append('chapter_no', String(chapterNo));
    const res = await fetch(`${BASE}/curriculum/courses/${courseId}/chapters/upload-pack`, {
      method: 'POST',
      headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) },
      body: fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || `Upload failed (${res.status})`);
    return data;
  },

  downloadDocx: async (id, step, day) => {
    const res = await fetch(
      `${BASE}/curriculum/chapters/${id}/output/${step}/docx${day != null ? `?day=${day}` : ''}`,
      { headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) } },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.message || `Export failed (${res.status})`);
    }
    return res.blob();
  },

  downloadWorkbookPack: async (id) => {
    const res = await fetch(
      `${BASE}/curriculum/chapters/${id}/export/workbook-pack?rebuild=1`,
      { headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) } },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const msg = d.message || `Workbook pack export failed (${res.status})`;
      throw new Error(d.details ? `${msg}\n\n${String(d.details).slice(0, 3000)}` : msg);
    }
    return res.blob();
  },

  downloadNotesPack: async (id) => {
    const res = await fetch(
      `${BASE}/curriculum/chapters/${id}/export/notes-pack`,
      { headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) } },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      const msg = d.message || `Notes pack export failed (${res.status})`;
      throw new Error(d.details ? `${msg}\n\n${String(d.details).slice(0, 3000)}` : msg);
    }
    return res.blob();
  },

  /** Merge Notes or MCQs PDFs for selected chapters into one printable PDF. pack: 'notes' | 'mcqs' */
  downloadCombinedNotesPdf: async (courseId, chapterIds, pack = 'notes') => {
    const res = await fetch(
      `${BASE}/curriculum/courses/${courseId}/export/combined-notes-pdf`,
      {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ chapterIds, pack }),
      },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.message || `Combined ${pack} PDF failed (${res.status})`);
    }
    return res.blob();
  },

  /** EduPage pack preview manifest (cached PDF + day PNGs). Never triggers AI.
   * Pass { rebuild: true } to bust server disk cache (e.g. after Update). */
  getPackPreview: (id, opts = {}) => {
    const q = opts.rebuild ? '?rebuild=1' : '';
    return req('GET', `/chapters/${id}/preview/pack${q}`);
  },

  fetchPackPreviewFileBlob: async (id, filename) => {
    const res = await fetch(
      `${BASE}/curriculum/chapters/${id}/preview/file/${encodeURIComponent(filename)}`,
      { headers: { ...(token() ? { Authorization: `Bearer ${token()}` } : {}) } },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.message || `Preview file failed (${res.status})`);
    }
    return res.blob();
  },
};

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadText(text, filename) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  downloadBlob(blob, filename);
}

export default plansPrepApi;
